import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import { responsiveStoreEnhancer } from 'redux-responsive';

import reducerRegistry from '@comeon/reducer-registry';
import authMiddleware from '../services/middleware/auth-middleware.js';
import defaultReducers from '../reducers/index.js';
import preloadedState from './preloaded-state.js';
import configuration from '../configuration/configuration.js';
import sportsbookDynamicMiddlewares from '../services/middleware/sportsbook-dynamic-middlewares';
import { errorReporterReduxMiddleware, getErrorReporterSessionId } from '@comeon/error-reporter';

const errorReporterMiddleware = errorReporterReduxMiddleware({
	stateTransformer: state => {
		return {
			brand: {
				brandName: state.brand.brandName,
				franchiseCode: state.brand.franchiseCode,
				license: state.brand.license
			},
			account: {
				balance: state.account.balance,
				currencyCode: state.account.currencyCode,
				id: state.account.id,
				playerId: state.account.playerId,
				points: state.account.points
			}
		};
	},
	actionTransformer: ({ type, ...payload }) => {
		// Prefix type so that its always shown in the error report
		return { _type: type, ...payload };
	},
	configureScopeWithState: (scope, state) => {
		scope.setTag('franchiseCode', state?.brand?.franchiseCode);
		scope.setTag('locale', state?.account?.locale);
		scope.setTag('brand', state?.brand?.brandName);

		const sessionId = getErrorReporterSessionId();

		if (state?.account?.id) {
			scope.setUser({ id: state?.account?.id, sessionId });
		} else {
			scope.setUser({ sessionId });
		}
	}
});

const middlewares = [authMiddleware(), sportsbookDynamicMiddlewares.enhancer, errorReporterMiddleware];

// Preserve initial state for not-yet-loaded reducers
const combine = reducers => {
	const reducerNames = Object.keys(reducers);
	const identity = (state = null) => state;
	Object.keys(preloadedState).forEach(item => {
		if (!reducerNames.includes(item)) {
			reducers[item] = identity; // eslint-disable-line no-param-reassign
		}
	});
	return combineReducers(reducers);
};

Object.entries(defaultReducers)
	.filter(r => (r[1] ? r : undefined))
	.map(([name, reducer]) => reducerRegistry.register(name, reducer));

const reducers = combine(reducerRegistry.getReducers());

const store = configureStore({
	reducer: reducers,
	preloadedState,
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({
			thunk: { extraArgument: configuration }
		}).concat(middlewares),
	enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(responsiveStoreEnhancer)
});

reducerRegistry.setChangeListener(newReducers => {
	store.replaceReducer(
		combine({
			...newReducers
		})
	);
});

export const observeStore = (selector, handler) => {
	let currentState;

	function handleChange() {
		const nextState = selector(store.getState());
		if (nextState !== currentState) {
			currentState = nextState;
			handler(currentState);
		}
	}

	handleChange();
	return {
		unsubscribe: store.subscribe(handleChange),
		dispatch: store.dispatch
	};
};

export default store;
